import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Accordion } from "react-bootstrap";
import * as bootstrap from "bootstrap";
import { useEvacuationLayers } from "../../hooks/useEvacuation.js";
import useEvacuationStore from "../../stores/evacuation-store.js";
import useAuthStore from "../../stores/auth-store.js";

const EvacuationPortalSelector = () => {
  // Get authentication status
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = !!user;
  console.log(`🛂 Evacuation Portal - User is ${isAuthenticated ? 'authenticated' : 'not authenticated'}`);
  
  // Determine if we are on the evacuation portal page
  const location = useLocation();
  const pathName = location.pathname.split("/")[2];
  const isPortalPage = pathName === "evacuation-portal";
  console.log("🌐 EvacuationPortalSelector: isPortalPage =", isPortalPage);

  // Get zones data from React Query
  const {
    data: geoJsonLayers,
    isLoading: isLoadingZones,
    isError: hasError,
    error: zonesError,
  }  = useEvacuationLayers(isAuthenticated, { enabled: isPortalPage && isAuthenticated });
  console.log("📦 Selector: Fetched geoJsonLayers:", geoJsonLayers);

  // Get layer visibility state from Zustand
  const visibleLayers = useEvacuationStore((state) => state.visibleLayers);
  const setVisibleLayers = useEvacuationStore(
    (state) => state.setVisibleLayers
  );
  const setStaticLayers = useEvacuationStore((state) => state.setStaticLayers);

  // Layer definitions (could be moved to a config file)
  const layerCategories = {
    points: {
      title: "Points Layers",
      layers: {
        Destinations_Muster_Stations: {
          label: "Destinations Muster Stations",
          description: "Show/hide destinations muster stations on the map",
          mandatory: false,
          display: true,
        },
        RDCO_Evac_Zone_Centroids_v1_358: {
          label: "TAZ Centroids",
          description: "Show/hide TAZ centroids on the map",
          mandatory: false,
        },
      },
    },
    network: {
      title: "Network Layers",
      layers: {},
    },
    zones: {
      title: "Zone Layers",
      layers: {
        RDCO_Evac_Zones_v1_358: {
          label: "TAZ",
          description: "Show/hide TAZ boundaries on the map",
          mandatory: true,
        },
      },
    },
  };
  const selectors = [
    "Destinations_Muster_Stations",
    "RDCO_Evac_Zones_v1_358",
    "RDCO_Evac_Zone_Centroids_v1_358",
  ];

  const getLayoutConfig = () => {
    if (pathName === "evacuation-portal") {
      return {
        flexDirection: "flex-column",
        colSize: "col-xl-12",
        colInnerSize: "col-lg-12",
        marginTop: "mt-3",
      };
    }
    return {
      flexDirection: "flex-column",
      colSize: "col-xl-12",
      colInnerSize: "col-lg-12",
      marginTop: "mt-3",
    };
  };
  const { flexDirection, colSize, colInnerSize, marginTop } = getLayoutConfig();

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;

    // Check if layer is mandatory
    let isMandatory = false;
    Object.values(layerCategories).forEach((category) => {
      if (category.layers[name]?.mandatory) {
        isMandatory = true;
      }
    });

    // Prevent unchecking mandatory layers
    if (isMandatory && !checked) {
      console.log(`Cannot uncheck mandatory layer: ${name}`);
      return;
    }

    // Update visibility in store
    setVisibleLayers({
      ...visibleLayers,
      [name]: checked,
    });
  };

  const getVisibleLayerKeys = () => {
    return Object.entries(visibleLayers)
      .filter(([key, isVisible]) => isVisible)
      .map(([key]) => key);
  };

  // Initialize Bootstrap popovers for info icons
  useEffect(() => {
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );
    const popoverInstances = [];

    if (popoverTriggerList.length > 0) {
      popoverTriggerList.forEach((elm) => {
        const popover = new bootstrap.Popover(elm);
        popoverInstances.push(popover);
      });
    }

    // Cleanup popovers on unmount
    return () => {
      popoverInstances.forEach((popover) => {
        try {
          popover.dispose();
        } catch (error) {
          console.warn("Error disposing popover:", error);
        }
      });
    };
  }, []);

  useEffect(() => {
    console.log("🌐 EvacuationPortalSelector: useEffect triggered for layer sync");
    if (!isPortalPage) {
      console.log("🌐 EvacuationPortalSelector: Not on evacuation-portal route, skipping layer sync");
      return;
    }

    if (geoJsonLayers && Object.keys(geoJsonLayers).length !== 0) {
      console.log(
        "🌐 EvacuationPortalSelector: Syncing static layers with selectors)"
      );

      // If any of the geoJsonLayers keys is not in selectors array, then remove from geoJsonLayers
      const filteredLayers = {};
      Object.keys(geoJsonLayers).forEach((key) => {
        if (selectors.includes(key)) {
          filteredLayers[key] = geoJsonLayers[key];
        }
      });
      setStaticLayers(filteredLayers);
    } else {
      return;
    }

    if (Object.keys(visibleLayers).length > 0) {
      return;
    }

    const newVisibleLayers = {};
    Object.values(layerCategories).forEach((category) => {
      Object.entries(category.layers).forEach(([key, definition]) => {
        newVisibleLayers[key] =
          definition.mandatory === true || definition.display === true;
      });
    });

    setVisibleLayers(newVisibleLayers);
  }, [geoJsonLayers, visibleLayers, setVisibleLayers, setStaticLayers]);

  if (!isPortalPage) {
    return null;
  }

  // Loading state
  if (isLoadingZones) {
    return (
      <div className="accordion mb-4" id="evacuationPortalAccordion">
        <div className="accordion-item bg-transparent border-0">
          <div className="accordion-body p-3">
            <div className="d-flex align-items-center text-warning">
              <div
                className="spinner-border spinner-border-sm me-2"
                role="status"
              >
                <span className="visually-hidden">Loading...</span>
              </div>
              <small>Loading available layers...</small>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (hasError) {
    return (
      <div className="accordion mb-4" id="evacuationPortalAccordion">
        <div className="accordion-item bg-transparent border-0">
          <div className="accordion-body p-3">
            <div className="alert alert-danger mb-0">
              <i className="fa fa-exclamation-circle me-2"></i>
              <small>Failed to load map layers: {zonesError?.message}</small>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Accordion
      defaultActiveKey="0"
      className="mb-4 ms-3 me-3"
      id="evacuationPortalAccordion"
    >
      <Accordion.Item eventKey="0" className="bg-transparent border-0">
        <Accordion.Header
          className="accordion-header-custom border rounded-top rounded-2 border-gray-700 border-1"
          style={{
            "--bs-accordion-btn-bg": "var(--bs-gray-900)",
            "--bs-accordion-btn-color": "var(--bs-white)",
            "--bs-accordion-active-bg": "var(--bs-gray-900)",
            "--bs-accordion-active-color": "var(--bs-white)",
            "--bs-accordion-btn-focus-border-color": "var(--bs-gray-700)",
            "--bs-accordion-btn-focus-box-shadow": "none",
          }}
        >
          <h5 className="text-warning fw-bolder m-0 d-flex align-items-center gap-2">
            <i className="bi bi-sliders"></i>
            Evacuation Zone Map Controls
          </h5>
        </Accordion.Header>
        <Accordion.Body className="p-0">
          <div
            className={`row gx-0 ${flexDirection} border rounded-bottom rounded-2 border-gray-700 border-1 p-3 overflow-hidden bg-gray-900 text-white`}
          >
            {/* Layer Controls */}
            <div className={`${colInnerSize}`}>
              <div className={`row row-gap-3 ${flexDirection}`}>
                {/* Points Layers */}
                {Object.keys(layerCategories.points.layers).length > 0 && (
                  <div className={`${colSize}`}>
                    <h5 className="mb-3 text-gray-400">
                      {layerCategories.points.title}
                    </h5>
                    <div className="mb-3">
                      {Object.entries(layerCategories.points.layers).map(
                        ([key, definition]) =>
                          selectors.includes(key) && (
                            <div key={key} className="form-check mb-2">
                              <input
                                type="checkbox"
                                id={key}
                                name={key}
                                checked={visibleLayers[key] || false}
                                onChange={handleCheckboxChange}
                                className="form-check-input"
                                disabled={definition.mandatory}
                              />
                              <label htmlFor={key} className="form-check-label">
                                {definition.label}
                                {definition.mandatory && (
                                  <span className="text-warning ms-1">
                                    (Required)
                                  </span>
                                )}
                              </label>
                            </div>
                          )
                      )}
                    </div>
                  </div>
                )}

                {/* Network Layers */}
                {Object.keys(layerCategories.network.layers).length > 0 && (
                  <div className={`${colSize}`}>
                    <h5 className="mb-3 text-gray-400">
                      {layerCategories.network.title}
                    </h5>
                    <div className="mb-3">
                      {Object.entries(layerCategories.network.layers).map(
                        ([key, definition]) =>
                          selectors.includes(key) && (
                            <div key={key} className="form-check mb-2">
                              <input
                                type="checkbox"
                                id={key}
                                name={key}
                                checked={visibleLayers[key] || false}
                                onChange={handleCheckboxChange}
                                className="form-check-input"
                                disabled={definition.mandatory}
                              />
                              <label htmlFor={key} className="form-check-label">
                                {definition.label}
                                {definition.mandatory && (
                                  <span className="text-warning ms-1">
                                    (Required)
                                  </span>
                                )}
                              </label>
                            </div>
                          )
                      )}
                    </div>
                  </div>
                )}

                {/* Zone Layers */}
                {Object.keys(layerCategories.zones.layers).length > 0 && (
                  <div className={`${colSize}`}>
                    <h5 className="mb-3 text-gray-400">
                      {layerCategories.zones.title}
                    </h5>
                    <div className="mb-3">
                      {Object.entries(layerCategories.zones.layers).map(
                        ([key, definition]) =>
                          selectors.includes(key) && (
                            <div key={key} className="form-check mb-2">
                              <input
                                type="checkbox"
                                id={key}
                                name={key}
                                checked={visibleLayers[key] || false}
                                onChange={handleCheckboxChange}
                                className="form-check-input"
                                disabled={definition.mandatory}
                              />
                              <label htmlFor={key} className="form-check-label">
                                {definition.label}
                                {definition.mandatory && (
                                  <span className="text-warning ms-1">
                                    (Required)
                                  </span>
                                )}
                              </label>
                            </div>
                          )
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Current Selection Display */}
            <div className={`${colInnerSize} ${marginTop}`}>
              <div className="d-flex flex-column">
                <h5 className="text-gray-500">Active Map Layers</h5>
                <div className="mt-2 d-flex flex-row flex-wrap gap-1">
                  {getVisibleLayerKeys().length > 0 ? (
                    Object.entries(layerCategories).map(
                      ([categoryKey, category]) =>
                        Object.entries(category.layers).map(
                          ([key, definition]) =>
                            visibleLayers[key] && (
                              <span
                                key={key}
                                className={`badge fw-bold fs-12px ${
                                  definition.mandatory
                                    ? "bg-warning bg-opacity-15 text-warning"
                                    : "bg-primary bg-opacity-15 text-primary"
                                }`}
                              >
                                {definition.label}
                                {definition.mandatory && " (Required)"}
                              </span>
                            )
                        )
                    )
                  ) : (
                    <span className="text-muted text-gray-500">
                      No layers selected
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
};

export default EvacuationPortalSelector;
